import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  Chip,
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  TablePagination,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  SearchOutlined,
  DeleteOutlined,
  AddCircleOutlined,
} from '@mui/icons-material';
import type {
  MachineMapping,
  RunStageStep,
  MachineSearchResult,
  LabCollection,
  PlanEdge,
} from '@/types/run-stage.types';
import {
  searchMachines,
  getUserLabs,
  updatePlan,
  updateExecuteStatusForBooking,
  deleteExecuteBooking,
  getRunPlan,
  getRunExecute,
  getPlanGraphEdges,
  getPreviewPlan,
} from '@services/runApi.service';
import { useRunStageStore } from '@/run-stage/store/runStageStore';

interface MapMachinesProps {
  edges?: PlanEdge[];
  steps: RunStageStep[];
  machines?: MachineMapping[];
  runId: string;
  runUid?: string;
  isReadOnly?: boolean;
}

export const MapMachines: React.FC<MapMachinesProps> = ({
  edges = [],
  steps,
  machines = [],
  runId,
  runUid,
  isReadOnly = false,
}) => {
  const refresh = useRunStageStore((s) => s.refresh);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEdge, setSelectedEdge] = useState<PlanEdge | null>(null);

  // Sidebar search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MachineSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [machineToBook, setMachineToBook] = useState<MachineSearchResult | null>(null);
  const [labs, setLabs] = useState<LabCollection[]>([]);
  const [selectedLab, setSelectedLab] = useState('');

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Derive display rows — prefer edges (like Angular), fallback to steps
  const displayRows = useMemo(() => {
    if (edges.length > 0) {
      return [...edges].sort((a, b) =>
        (a.properties?.orderString ?? '').localeCompare(b.properties?.orderString ?? '')
      );
    }
    // Fallback: build pseudo-edges from steps
    return steps
      .filter((s) => s.archetype !== 'material' && s.archetype !== 'container')
      .map((s) => ({
        stepId: s.stepId,
        cloneNumber: s.cloneNumber,
        properties: {
          orderString: String(s.order ?? ''),
          actionType: s.stepName,
          subType: s.subType,
          cycleOrder: undefined,
        },
        machines: {
          booking: machines.find(
            (m) => m.stepId === s.stepId && (m.cloneNumber ?? 0) === (s.cloneNumber ?? 0)
          )
            ? {
                machineId: machines.find(
                  (m) => m.stepId === s.stepId && (m.cloneNumber ?? 0) === (s.cloneNumber ?? 0)
                )?.machineId,
                machineName: machines.find(
                  (m) => m.stepId === s.stepId && (m.cloneNumber ?? 0) === (s.cloneNumber ?? 0)
                )?.machineName,
              }
            : undefined,
        },
        completed: s.completed,
      })) as PlanEdge[];
  }, [edges, steps, machines]);

  const paginatedRows = displayRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Load labs on mount
  useEffect(() => {
    getUserLabs()
      .then(setLabs)
      .catch(() => {});
  }, []);

  // Search instruments
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const results = await searchMachines(searchQuery, selectedLab || undefined);
      // Prepend recommended instruments from selected edge
      if (selectedEdge?.recommendedInstruments?.length) {
        const recIds = new Set(selectedEdge.recommendedInstruments.map((r) => r._id));
        const recommended = results
          .filter((r) => recIds.has(r._id))
          .map((r) => ({ ...r, recommended: true }));
        const others = results.filter((r) => !recIds.has(r._id));
        setSearchResults([...recommended, ...others]);
      } else {
        setSearchResults(results);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, selectedLab, selectedEdge]);

  // Book machine to selected edge/step
  const handleBookMachine = useCallback(async () => {
    if (!machineToBook || !selectedEdge || !runId) return;
    setActionLoading(true);
    setActionError(null);
    try {
      // Update plan with machine mapping
      await updatePlan({
        runId,
        runUid,
        steps: [
          {
            stepId: selectedEdge.stepId,
            cloneNumber: selectedEdge.cloneNumber ?? 0,
            machines: {
              booking: {
                machineId: machineToBook._id,
                machineName: machineToBook.name,
              },
            },
          },
        ],
      });

      // Update execute status with machine info
      await updateExecuteStatusForBooking({
        runId,
        runUid,
        steps: [
          {
            stepId: selectedEdge.stepId,
            cloneNumber: selectedEdge.cloneNumber ?? 0,
            machines: {
              booking: {
                machineId: machineToBook._id,
                machineName: machineToBook.name,
              },
            },
          },
        ],
      });

      setMachineToBook(null);
      setSelectedEdge(null);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to book instrument');
    } finally {
      setActionLoading(false);
    }
  }, [machineToBook, selectedEdge, runId, runUid, refresh]);

  // Delete machine booking
  const handleDeleteBooking = useCallback(
    async (edge: PlanEdge) => {
      if (!runId) return;
      setActionLoading(true);
      setActionError(null);
      try {
        await deleteExecuteBooking({
          stepId: edge.stepId!,
          cloneNumber: edge.cloneNumber ?? 0,
          operationCloneNumber: edge.operationCloneNumber,
          runId,
          runUid,
        });
        await refresh();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete booking');
      } finally {
        setActionLoading(false);
      }
    },
    [runId, runUid, refresh]
  );

  if (!displayRows.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No operation steps found for this run.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left column — Machine/Step Table */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {actionError && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto', m: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {!isReadOnly && (
                  <TableCell padding="checkbox" sx={{ fontWeight: 600, width: 40 }} />
                )}
                <TableCell sx={{ fontWeight: 600 }}>Step No.</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Step Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Instrument Mapped</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((edge, idx) => {
                const isSelected = selectedEdge?.stepId === edge.stepId &&
                  (selectedEdge?.cloneNumber ?? 0) === (edge.cloneNumber ?? 0);
                const booking = edge.machines?.booking;
                const hasBooking = Boolean(booking?.machineId || booking?.machineName);
                const stepName = [
                  edge.properties?.actionType,
                  edge.properties?.subType && edge.properties.subType !== edge.properties.actionType
                    ? `(${edge.properties.subType})`
                    : '',
                  edge.properties?.cycleOrder ? `x${edge.properties.cycleOrder}` : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <TableRow
                    key={`${edge.stepId}-${edge.cloneNumber ?? 0}-${idx}`}
                    hover
                    selected={isSelected}
                    onClick={() => !isReadOnly && setSelectedEdge(edge)}
                    sx={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                  >
                    {!isReadOnly && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          disabled={edge.completed}
                          readOnly
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {edge.properties?.orderString || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{stepName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {booking?.duration || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        {hasBooking ? (
                          <>
                            <Typography variant="body2">
                              {booking?.machineName || booking?.machineId || '—'}
                            </Typography>
                            {!isReadOnly && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBooking(edge);
                                }}
                                disabled={actionLoading}
                                aria-label="Delete booking"
                              >
                                <DeleteOutlined fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {booking?.startTime ? new Date(booking.startTime).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {booking?.endTime ? new Date(booking.endTime).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={displayRows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ flexShrink: 0 }}
        />
      </Box>

      {/* Right column — Instrument Search Sidebar */}
      <Box
        sx={{
          width: '30%',
          minWidth: 260,
          maxWidth: 380,
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Instrument Search
          </Typography>

          {/* Search input */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search instruments…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              fullWidth
              slotProps={{ input: { sx: { fontSize: '0.85rem' } } }}
            />
            <IconButton size="small" onClick={handleSearch} disabled={searchLoading}>
              {searchLoading ? <CircularProgress size={18} /> : <SearchOutlined />}
            </IconButton>
          </Stack>

          {/* Lab filter */}
          {labs.length > 0 && (
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Lab Filter</InputLabel>
              <Select
                value={selectedLab}
                onChange={(e) => setSelectedLab(e.target.value)}
                label="Lab Filter"
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="">All Labs</MenuItem>
                {labs.map((lab) => (
                  <MenuItem key={lab._id} value={lab._id}>
                    {lab.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Book button */}
          {!isReadOnly && machineToBook && selectedEdge && (
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleBookMachine}
              disabled={actionLoading}
              sx={{ textTransform: 'none' }}
            >
              {actionLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Book "{machineToBook.name}"
            </Button>
          )}
        </Box>

        {/* Search results */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {searchResults.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {searchResults.length} result(s)
              </Typography>
              {searchResults.map((machine) => (
                <Paper
                  key={machine._id}
                  variant="outlined"
                  sx={{
                    p: 1,
                    mb: 0.5,
                    cursor: 'pointer',
                    backgroundColor:
                      machineToBook?._id === machine._id ? 'action.selected' : 'background.paper',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() =>
                    setMachineToBook(machineToBook?._id === machine._id ? null : machine)
                  }
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={machineToBook?._id === machine._id}
                      sx={{ p: 0 }}
                      readOnly
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {machine.name || machine._id}
                        </Typography>
                        {machine.recommended && (
                          <Chip
                            label="Recommended"
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[
                          machine.uid && `UID: ${machine.uid}`,
                          machine.customId && `ID: ${machine.customId}`,
                          machine.collection,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      {(machine.location || machine.lastCalibrationDate) && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {[
                            machine.location && `Storage Location: ${machine.location}`,
                            machine.lastCalibrationDate &&
                              `Last Cal: ${new Date(machine.lastCalibrationDate).toLocaleDateString()}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </>
          )}

          {searchResults.length === 0 && searchQuery && !searchLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              No instruments found
            </Typography>
          )}

          {!searchQuery && !searchLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {selectedEdge
                ? 'Search for instruments to book for the selected step'
                : 'Select a step from the table, then search for instruments'}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
